import { trpc } from "@/lib/trpc";
import { formatBJ, checkTradingHours, smartInterval } from "@/lib/timeUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  Zap,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Activity,
  Filter,
} from "lucide-react";
import { useState, useMemo } from "react";

const LOG_TYPE_MAP: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  signal_generated: { label: "信号生成", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: <Zap className="w-3.5 h-3.5" /> },
  order_submitted: { label: "委托提交", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: <Clock className="w-3.5 h-3.5" /> },
  order_filled: { label: "委托成交", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  order_rejected: { label: "委托拒绝", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: <XCircle className="w-3.5 h-3.5" /> },
  order_cancelled: { label: "委托撤销", color: "bg-gray-500/20 text-gray-400 border-gray-500/30", icon: <Ban className="w-3.5 h-3.5" /> },
  position_opened: { label: "建仓", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: <ArrowUpCircle className="w-3.5 h-3.5" /> },
  position_closed: { label: "平仓", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: <ArrowDownCircle className="w-3.5 h-3.5" /> },
  take_profit: { label: "止盈触发", color: "bg-teal-500/20 text-teal-400 border-teal-500/30", icon: <TrendingUp className="w-3.5 h-3.5" /> },
  stop_loss: { label: "止损触发", color: "bg-rose-500/20 text-rose-400 border-rose-500/30", icon: <TrendingDown className="w-3.5 h-3.5" /> },
  circuit_breaker: { label: "熔断触发", color: "bg-red-600/20 text-red-300 border-red-600/30", icon: <ShieldAlert className="w-3.5 h-3.5" /> },
  system_error: { label: "系统异常", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  manual_operation: { label: "手动操作", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: <Activity className="w-3.5 h-3.5" /> },
};

const EXEC_STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: "待处理", color: "bg-yellow-500/20 text-yellow-400" },
  success: { label: "成功", color: "bg-green-500/20 text-green-400" },
  failed: { label: "失败", color: "bg-red-500/20 text-red-400" },
  cancelled: { label: "已取消", color: "bg-gray-500/20 text-gray-400" },
};

const PAGE_SIZE = 20;

export default function TradeLogs() {
  const [logTypeFilter, setLogTypeFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);

  const filters = useMemo(() => ({
    logType: logTypeFilter !== "all" ? logTypeFilter : undefined,
    direction: directionFilter !== "all" ? directionFilter : undefined,
    executionStatus: statusFilter !== "all" ? statusFilter : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }), [logTypeFilter, directionFilter, statusFilter, page]);

  const _isTH = checkTradingHours().isTradingHours;
  const { data, isLoading, refetch } = trpc.tradeLogs.list.useQuery(filters, {
    refetchInterval: smartInterval(15000, _isTH, 60000),
  });
  const { data: stats } = trpc.tradeLogs.stats.useQuery(undefined, {
    refetchInterval: smartInterval(30000, _isTH, 120000),
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleResetFilters = () => {
    setLogTypeFilter("all");
    setDirectionFilter("all");
    setStatusFilter("all");
    setPage(0);
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">交易日志</h1>
          <p className="text-sm text-muted-foreground mt-1">详细记录每笔交易的完整生命周期，包含信号生成、委托执行、持仓变动和市场环境快照</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          刷新
        </Button>
      </div>

      {/* 统计概览 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">总日志数</div>
              <div className="text-xl font-bold text-foreground mt-1">{stats.totalLogs}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">今日日志</div>
              <div className="text-xl font-bold text-blue-400 mt-1">{stats.todayLogs}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">信号生成</div>
              <div className="text-xl font-bold text-cyan-400 mt-1">{stats.signalCount}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">委托提交</div>
              <div className="text-xl font-bold text-yellow-400 mt-1">{stats.orderCount}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">成交笔数</div>
              <div className="text-xl font-bold text-green-400 mt-1">{stats.filledCount}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">异常次数</div>
              <div className="text-xl font-bold text-red-400 mt-1">{stats.errorCount}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 筛选栏 */}
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              筛选：
            </div>
            <Select value={logTypeFilter} onValueChange={(v) => { setLogTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[150px] h-8 text-xs">
                <SelectValue placeholder="日志类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部类型</SelectItem>
                <SelectItem value="signal_generated">信号生成</SelectItem>
                <SelectItem value="order_submitted">委托提交</SelectItem>
                <SelectItem value="order_filled">委托成交</SelectItem>
                <SelectItem value="order_rejected">委托拒绝</SelectItem>
                <SelectItem value="order_cancelled">委托撤销</SelectItem>
                <SelectItem value="position_opened">建仓</SelectItem>
                <SelectItem value="position_closed">平仓</SelectItem>
                <SelectItem value="take_profit">止盈触发</SelectItem>
                <SelectItem value="stop_loss">止损触发</SelectItem>
                <SelectItem value="circuit_breaker">熔断触发</SelectItem>
                <SelectItem value="system_error">系统异常</SelectItem>
                <SelectItem value="manual_operation">手动操作</SelectItem>
              </SelectContent>
            </Select>
            <Select value={directionFilter} onValueChange={(v) => { setDirectionFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="方向" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部方向</SelectItem>
                <SelectItem value="buy">买入</SelectItem>
                <SelectItem value="sell">卖出</SelectItem>
                <SelectItem value="none">无方向</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部状态</SelectItem>
                <SelectItem value="pending">待处理</SelectItem>
                <SelectItem value="success">成功</SelectItem>
                <SelectItem value="failed">失败</SelectItem>
                <SelectItem value="cancelled">已取消</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={handleResetFilters} className="text-xs h-8">
              重置
            </Button>
            <div className="ml-auto text-xs text-muted-foreground">
              共 {total} 条记录
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 日志列表 */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            交易日志明细
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              加载中...
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-10 h-10 mb-3 opacity-30" />
              <p>暂无交易日志</p>
              <p className="text-xs mt-1">生成交易信号或执行交易后，日志将自动记录在此</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {logs.map((log) => {
                  const typeInfo = LOG_TYPE_MAP[log.logType] || { label: log.logType, color: "bg-gray-500/20 text-gray-400", icon: <Activity className="w-3.5 h-3.5" /> };
                  const statusInfo = EXEC_STATUS_MAP[log.executionStatus] || { label: log.executionStatus, color: "bg-gray-500/20 text-gray-400" };
                  const time = formatBJ(log.createdAt, "datetime");

                  return (
                    <div key={log.id} className="group rounded-lg border border-border/40 bg-background/50 hover:border-border/70 transition-colors">
                      {/* 主行 */}
                      <div className="flex items-center gap-3 px-4 py-3">
                        {/* 时间 */}
                        <div className="text-xs text-muted-foreground w-[100px] shrink-0 font-mono">
                          {time}
                        </div>

                        {/* 日志类型 */}
                        <Badge variant="outline" className={`${typeInfo.color} text-[11px] gap-1 shrink-0`}>
                          {typeInfo.icon}
                          {typeInfo.label}
                        </Badge>

                        {/* 方向 */}
                        {log.direction !== "none" && (
                          <Badge variant="outline" className={`text-[11px] shrink-0 ${
                            log.direction === "buy"
                              ? "bg-gain text-gain border-gain/30"
                              : "bg-loss text-loss border-loss/30"
                          }`}>
                            {log.direction === "buy" ? "买入" : "卖出"}
                          </Badge>
                        )}

                        {/* 股票 */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-sm font-medium text-foreground">{log.stockName}</span>
                          <span className="text-xs text-muted-foreground">{log.stockCode}</span>
                        </div>

                        {/* 价格和数量 */}
                        {log.price > 0 && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                            <span>¥{log.price.toFixed(2)}</span>
                            {log.quantity > 0 && <span>× {log.quantity}股</span>}
                            {log.amount > 0 && <span className="text-foreground/70">= ¥{log.amount.toFixed(0)}</span>}
                          </div>
                        )}

                        {/* 盈亏 */}
                        {log.realizedPnl !== 0 && (
                          <span className={`text-xs font-medium shrink-0 ${log.realizedPnl > 0 ? "text-gain" : "text-loss"}`}>
                            {log.realizedPnl > 0 ? "+" : ""}{log.realizedPnl.toFixed(2)}
                          </span>
                        )}

                        {/* 弹性空间 */}
                        <div className="flex-1 min-w-0" />

                        {/* 执行状态 */}
                        <Badge variant="outline" className={`${statusInfo.color} text-[11px] shrink-0`}>
                          {statusInfo.label}
                        </Badge>

                        {/* 模拟标记 */}
                        {log.isDryRun === 1 && (
                          <Badge variant="outline" className="bg-purple-500/15 text-purple-400 border-purple-500/30 text-[10px] shrink-0">
                            模拟
                          </Badge>
                        )}
                      </div>

                      {/* 详情行（展开） */}
                      <div className="hidden group-hover:block px-4 pb-3 pt-0 border-t border-border/30">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs mt-2">
                          {/* 布林带 */}
                          {(log.bbUpper > 0 || log.bbMiddle > 0 || log.bbLower > 0) && (
                            <div>
                              <span className="text-muted-foreground">布林带：</span>
                              <span className="text-foreground/80">
                                {log.bbLower.toFixed(2)} / {log.bbMiddle.toFixed(2)} / {log.bbUpper.toFixed(2)}
                              </span>
                            </div>
                          )}

                          {/* 持仓变动 */}
                          {(log.positionBefore > 0 || log.positionAfter > 0) && (
                            <div>
                              <span className="text-muted-foreground">持仓：</span>
                              <span className="text-foreground/80">
                                {log.positionBefore} → {log.positionAfter}
                              </span>
                            </div>
                          )}

                          {/* 均价变动 */}
                          {(log.avgCostBefore > 0 || log.avgCostAfter > 0) && (
                            <div>
                              <span className="text-muted-foreground">均价：</span>
                              <span className="text-foreground/80">
                                ¥{log.avgCostBefore.toFixed(2)} → ¥{log.avgCostAfter.toFixed(2)}
                              </span>
                            </div>
                          )}

                          {/* 手续费 */}
                          {log.fee > 0 && (
                            <div>
                              <span className="text-muted-foreground">手续费：</span>
                              <span className="text-foreground/80">¥{log.fee.toFixed(2)}</span>
                            </div>
                          )}

                          {/* 市场环境 */}
                          {log.indexValue > 0 && (
                            <div>
                              <span className="text-muted-foreground">上证指数：</span>
                              <span className={`${log.indexChange >= 0 ? "text-gain" : "text-loss"}`}>
                                {log.indexValue.toFixed(0)} ({log.indexChange >= 0 ? "+" : ""}{log.indexChange.toFixed(2)}%)
                              </span>
                            </div>
                          )}

                          {/* 市场状态 */}
                          {log.marketStatus !== "normal" && (
                            <div>
                              <span className="text-muted-foreground">市场状态：</span>
                              <span className="text-red-400">{log.marketStatus === "circuit_breaker" ? "熔断中" : log.marketStatus}</span>
                            </div>
                          )}

                          {/* 委托号 */}
                          {log.orderId && (
                            <div>
                              <span className="text-muted-foreground">委托号：</span>
                              <span className="text-foreground/80 font-mono">{log.orderId}</span>
                            </div>
                          )}

                          {/* 信号来源 */}
                          {log.signalSource && (
                            <div>
                              <span className="text-muted-foreground">信号源：</span>
                              <span className="text-foreground/80">{log.signalSource}</span>
                            </div>
                          )}
                        </div>

                        {/* 原因/备注 */}
                        {log.reason && (
                          <div className="mt-2 text-xs">
                            <span className="text-muted-foreground">原因：</span>
                            <span className="text-foreground/70">{log.reason}</span>
                          </div>
                        )}
                        {log.errorMessage && (
                          <div className="mt-1 text-xs">
                            <span className="text-red-400">错误：</span>
                            <span className="text-red-400/80">{log.errorMessage}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/30">
              <div className="text-xs text-muted-foreground">
                第 {page + 1} / {totalPages} 页
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  className="h-7 text-xs"
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  className="h-7 text-xs"
                >
                  下一页
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
